/**
 * Contact state-machine types for the Amazon Connect integration.
 *
 * Every Connect contact is classified once at registration and driven through
 * a pure state machine.  The machine emits commands that the hook translates
 * into React state / Redux dispatches / SSE stream calls.
 */

import type { ConnectContact } from '@/types';

// ---------------------------------------------------------------------------
// Classification (computed once, never changes)
// ---------------------------------------------------------------------------

export type ContactKind = 'voice' | 'callback' | 'chat' | 'monitor';

// ---------------------------------------------------------------------------
// Lifecycle stage
// ---------------------------------------------------------------------------

export type ContactStage =
  | 'pending' // registered, awaiting first lifecycle event
  | 'incoming' // modal visible (normalises INCOMING / CONNECTING / PENDING)
  | 'accepted' // agent accepted (callbacks are auto-accepted by Connect)
  | 'connected' // live conversation
  | 'afterCallWork' // chat ended or explicit ACW state
  | 'ended' // voice ended, Redux session kept alive for post-call docs
  | 'missed'
  | 'destroyed';

/** Stages from which no further transitions are allowed. */
export const TERMINAL_STAGES: ReadonlySet<ContactStage> = new Set([
  'ended',
  'missed',
  'destroyed',
  'afterCallWork',
]);

// ---------------------------------------------------------------------------
// Events that drive transitions
// ---------------------------------------------------------------------------

export type ContactEvent =
  | { type: 'delivered' }
  | { type: 'state_incoming' } // onIncoming | onConnecting | onPending (normalised)
  | { type: 'accepted' }
  | { type: 'connected' }
  | { type: 'acw' }
  | { type: 'ended'; inAcw: boolean }
  | { type: 'missed' }
  | { type: 'error' } // contact.onError — ringtone stop, customer decline
  | { type: 'destroyed' };

// ---------------------------------------------------------------------------
// Contact record
// ---------------------------------------------------------------------------

export interface ContactRecord {
  contact: ConnectContact;
  id: string; // getContactId()
  callScopeId: string; // getCallScopeContactId()
  kind: ContactKind;
  inbound: boolean;
  dialpadOutbound: boolean; // isDialContact → skip auto-accept
  stage: ContactStage;
  streamInitiated: boolean; // replaces outboundStreamInitiated Set
  callbackAccepted: boolean; // "dialing customer" phase for callbacks
  /** Timestamp when the record was first created (for timer display). */
  createdAt: number;
}

// ---------------------------------------------------------------------------
// Commands emitted by the machine
// ---------------------------------------------------------------------------

export type ContactCommand =
  | { op: 'setIncoming'; id: string }
  | { op: 'clearIncoming' }
  | { op: 'clearContactInformation' }
  | { op: 'fetchMetadata'; callScopeId: string }
  | {
      op: 'registerSession';
      callScopeId: string;
      sessionType: 'voice' | 'chat';
      startedAt?: number;
      focus?: boolean;
    }
  | { op: 'initiateStream'; callScopeId: string }
  | { op: 'setAfterCallWork'; id: string }
  | { op: 'removeFromActive'; id: string }
  | { op: 'setAgentNotRoutable' }
  | { op: 'clearConnectContact'; id: string }
  | { op: 'autoAcceptContact'; id: string }
  | { op: 'handleChatAccepted'; id: string }
  | { op: 'clearContactStream'; callScopeId: string };

// ---------------------------------------------------------------------------
// Snapshot consumed by the React layer
// ---------------------------------------------------------------------------

export interface ContactsSnapshot {
  active: ContactRecord[];
  /** contactId of the single incoming contact (or null). */
  incomingId: string | null;
  /** contactIds awaiting "Finish" — voice/callback ended + chat ACW. */
  pendingFinishIds: string[];
}
